import { CommunicationLanguage, ContactLanguages, ViewCommunicationItem } from 'briefpoint-client';
import Sparkle from 'components/Sparkle';
import { useDocumentApi } from 'hooks/useApi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import type { IntLi } from '.';
import InterrogatoryListItem from './InterrogatoryListItem';
import InterrogatoriesNavigator from './InterrogatoriesNavigator';
import './styles.scss';
import { useParams } from 'react-router-dom';
import { Check } from 'react-bootstrap-icons';

type IntComms = { [intId: string]: { [lang: string]: string } };

export default function InterrogatoriesList({
  canSimplify,
  clientLanguage,
  clientName,
  documentName,
  communicationItems,
  onClickNext,
  interrogatories,
  onSaveCommunicationItem,
  onSelect,
  onUpdateLanguage,
  questionType,
  readOnly,
  selectedInterrogatories,
  onTranslate,
  isSubmitting,
  canSpanish,
}: {
  interrogatories: IntLi[];
  communicationItems: Record<string, ViewCommunicationItem>;
  onSaveCommunicationItem: (
    intId: string,
    lang: CommunicationLanguage,
    val: string,
    id?: string
  ) => Promise<ViewCommunicationItem | undefined>;
  clientName: string;
  documentName: string;
  questionType: string;
  onClickNext: () => void;
  readOnly: boolean;
  onSelect: (id: number | null) => void;
  clientLanguage: ContactLanguages;
  onUpdateLanguage: (lang: ContactLanguages) => void;
  selectedInterrogatories: Set<number>;
  canSimplify: boolean;
  onTranslate: (id: string) => Promise<void>;
  isSubmitting: boolean;
  canSpanish: boolean;
}) {
  const [linkRef, setLinkRefs] = useState<{ [key: string]: React.RefObject<HTMLDivElement> }>({});
  const [refsDone, setRefsDone] = useState<boolean>(false);
  const [blockNext, setBlockNext] = useState<boolean>(false);

  const updateLinkRefs = (newRefs: { [key: string]: React.RefObject<HTMLDivElement> }) => {
    setLinkRefs(newRefs);
    setRefsDone(true);
  };

  const scrollToView = (target: string) => {
    const ref = linkRef[target];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="mb-4">
      <h1 className="feedback-header">Request Client Response</h1>
      {!readOnly ? (
        <p>Select the questions you'd like to send to your client for response.</p>
      ) : (
        <p>
          The communication has been opened by the client. Please start over to begin another communication
          for this document.
        </p>
      )}
      <Row className="gy-3">
        <Col sm={12} md={2}>
          <div className="fw-bold fs-4">Responses</div>
          <InterrogatoriesNavigator
            selectedInterrogatories={selectedInterrogatories}
            intData={interrogatories}
            onClick={scrollToView}
            refsDone={refsDone}
          />
        </Col>
        <Col>
          <div className="fw-bold fs-4">{documentName}</div>
          <div className="frogs-bounds">
            <div
              className="document-preview-container list-column frogs-list"
              style={{ paddingRight: '0px' }}
            >
              <ListWrapper
                canSimplify={canSimplify}
                communicationItems={communicationItems}
                // documentId={documentId}
                interrogatories={interrogatories}
                clientLanguage={clientLanguage}
                onSaveCommunicationItem={onSaveCommunicationItem}
                onSelect={onSelect}
                questionType={questionType}
                readOnly={readOnly}
                selectedInterrogatories={selectedInterrogatories}
                setLinkRefs={updateLinkRefs}
                onTranslate={onTranslate}
                onUpdateLanguage={onUpdateLanguage}
                canSpanish={canSpanish}
                setBlockNext={setBlockNext}
              />
            </div>
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button disabled={isSubmitting || blockNext} onClick={onClickNext}>
              Next
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}

function ListWrapper({
  interrogatories,
  communicationItems,
  onSaveCommunicationItem,
  setLinkRefs,
  questionType,
  readOnly,
  clientLanguage,
  onSelect,
  selectedInterrogatories,
  canSimplify,
  onTranslate,
  onUpdateLanguage,
  canSpanish,
  setBlockNext,
}: {
  interrogatories: IntLi[];
  communicationItems: Record<string, ViewCommunicationItem>;
  onSaveCommunicationItem: (
    intId: string,
    lang: CommunicationLanguage,
    val: string,
    id?: string
  ) => Promise<ViewCommunicationItem | undefined>;
  setLinkRefs: (newRefs: { [key: string]: React.RefObject<HTMLDivElement> }) => void;
  questionType: string;
  readOnly: boolean;
  clientLanguage?: ContactLanguages;
  onSelect: (id: number | null) => void;
  selectedInterrogatories: Set<number>;
  canSimplify: boolean;
  onTranslate: (id: string) => Promise<void>;
  onUpdateLanguage: (lang: ContactLanguages) => void;
  canSpanish: boolean;
  setBlockNext: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { documentId } = useParams<{ documentId: string }>();
  const refs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});
  const documentApi = useDocumentApi();
  const [simplifyingAll, setSimplifyingAll] = useState(false);
  const [simplifiedSet, setSimplifiedSet] = useState(new Set<number>()); // temporary success holding state
  const [savingSet, setSavingSet] = useState(new Set<number>()); // items for the most part hold their own saving state, but this is we are forcing saving on the top level

  useEffect(() => {
    if (interrogatories.length < 1) {
      return;
    }
    interrogatories.forEach((item) => {
      if (!refs.current[`${item.number}`]) {
        refs.current[`${item.number}`] = React.createRef();
      }
    });
    setLinkRefs(refs.current);
  }, [interrogatories, setLinkRefs]);

  const handleSimplifyOne = async (
    interrogatoryId: number,
    english?: ViewCommunicationItem,
    spanish?: ViewCommunicationItem
  ) => {
    try {
      const commItem = await documentApi
        .documentTranslateToPlain({
          documentId,
          interrogatoryId,
        })
        .then((simpleText) =>
          onSaveCommunicationItem(
            interrogatoryId.toString(),
            CommunicationLanguage.EN,
            `${english?.communication ? english?.communication + '\n\n' : ''}${simpleText}`,
            english?.id
          )
        );
      if (clientLanguage === ContactLanguages.Spanish && commItem && (!spanish || spanish?.isGenerated)) {
        onTranslate(commItem.id!);
      }
      setSimplifiedSet((prev) => new Set(prev.add(interrogatoryId)));
    } catch {
      console.log(`Error: Unable to simplify interrogatory with id ${interrogatoryId}`);
    }
  };

  const handleSimplifyAll = async () => {
    setSimplifyingAll(true);
    setBlockNext(true);
    // Fire the first five requests right away
    const intIdArray = Array.from(selectedInterrogatories).filter((x) => !simplifiedSet.has(x));
    const firstFive = intIdArray.slice(0, 5).map((li) => handleSimplifyOne(li, ...getCommItems(li)));
    await Promise.all(firstFive);

    // For the rest, wait for a second in between each one
    for (const li of intIdArray.slice(5)) {
      await handleSimplifyOne(li, ...getCommItems(li));
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    setSimplifyingAll(false);
    setBlockNext(false);
  };

  // Communication Items by language by interrogatory id
  const intComms = useMemo(() => {
    return Object.values(communicationItems).reduce((acc, cur) => {
      const intId = cur.objectId;
      if (intId && cur.language && cur.id) {
        let existing = acc[intId];
        if (existing) {
          acc[intId] = { ...existing, [cur.language]: cur.id };
        } else {
          acc[intId] = { [cur.language]: cur.id };
        }
      }
      return acc;
    }, {} as IntComms);
  }, [communicationItems]);

  const getCommItems = useCallback(
    (intId: number) => {
      return [communicationItems[intComms[intId]?.['EN']], communicationItems[intComms[intId]?.['ES']]];
    },
    [communicationItems, intComms]
  );

  const commItemNeedsTranslating = (english?: ViewCommunicationItem, spanish?: ViewCommunicationItem) => {
    return english?.communication && (!spanish || spanish.isGenerated) ? english : undefined;
  };

  const translateBatchItem = useCallback(
    async (item: ViewCommunicationItem) => {
      const intId = Number.parseInt(item.objectId!);
      setSavingSet((s) => new Set(s.add(intId)));
      await onTranslate(item.id!);
      setSavingSet((s) => {
        const update = new Set(s);
        update.delete(intId);
        return update;
      });
    },
    [onTranslate]
  );

  /**
   * Translates all communications to client  from English to Spanish in batches.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when all communications have been translated.
   *
   * @remarks
   * - It waits for one second between processing each batch to avoid overwhelming the translation API.
   */
  const handleTranslateAllClientResponses = useCallback(async () => {
    const BATCH_SIZE = 3;
    const intIdArray = Array.from(selectedInterrogatories);
    const commItems = intIdArray.map((x) => getCommItems(x));
    let needsTranslation = commItems.map((x) => commItemNeedsTranslating(...x)).filter((x) => x);

    while (needsTranslation.length) {
      const batch = needsTranslation.splice(0, BATCH_SIZE);
      await Promise.all(batch.map((li) => translateBatchItem(li!)));
    }
    await new Promise((resolve) => setTimeout(resolve, 1000)); // wait one second
  }, [getCommItems, selectedInterrogatories, translateBatchItem]);

  const updateClientLanguage = async (newLanguage: ContactLanguages) => {
    setBlockNext(true);
    onUpdateLanguage(newLanguage);
    if (newLanguage === ContactLanguages.Spanish) {
      await handleTranslateAllClientResponses();
    }
    setBlockNext(false);
  };

  const getSimplifyButton = () => {
    if (simplifiedSet.size >= selectedInterrogatories.size) {
      return (
        <span>
          <Check className={'text-success'} />
          &nbsp;Simplified All
        </span>
      );
    } else {
      return (
        <Button
          size="sm"
          variant={'primary'}
          className={`${simplifyingAll ? 'suggesting' : ''}`}
          onMouseDown={handleSimplifyAll} // mouseDown runs before onBlur
          disabled={simplifyingAll}
        >
          <Sparkle sparkling={simplifyingAll} />
          &nbsp;
          {simplifyingAll ? 'Simplifying...' : 'Simplify All'}
        </Button>
      );
    }
  };

  return (
    <div className="interrogatories-edit">
      {canSpanish && (
        <Card className="border-start-0 border-end-0 border-top-0 border-bottom-1">
          <Card.Body>
            <label className="fs-6 fw-bold d-block">Client’s Primary Language</label>
            <div title={`${simplifyingAll ? 'Please wait until all requests are simplified' : ''}`}>
              <Form.Check
                label="English"
                type="radio"
                inline
                checked={clientLanguage === ContactLanguages.EnglishUs}
                onChange={() => updateClientLanguage(ContactLanguages.EnglishUs)}
                disabled={readOnly || simplifyingAll}
                title={`${simplifyingAll ? 'Please wait until all requests are simplified' : ''}`}
                id="select-english"
              />
              <Form.Check
                label="Spanish"
                type="radio"
                inline
                checked={clientLanguage === ContactLanguages.Spanish}
                onChange={() => updateClientLanguage(ContactLanguages.Spanish)}
                disabled={readOnly || simplifyingAll}
                title={`${simplifyingAll ? 'Please wait until all requests are simplified' : ''}`}
                id="select-spanish"
              />
            </div>
          </Card.Body>
        </Card>
      )}
      <Card className="border-0">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
          <Button disabled={readOnly} variant="link" onClick={() => onSelect(null)} className="px-0">
            {selectedInterrogatories.size ? 'Deselect' : 'Select'} All Responses
          </Button>
          {!!selectedInterrogatories.size && canSimplify && !readOnly && getSimplifyButton()}
          {readOnly && <small>Read-only</small>}
        </Card.Header>
        <ul style={{ listStyleType: 'none', padding: 0 }}>
          {interrogatories.map((int) => (
            <li key={int.id} className="selection-item">
              <InterrogatoryListItem
                interrogatory={int}
                english={communicationItems[intComms[int.id]?.['EN']]}
                spanish={communicationItems[intComms[int.id]?.['ES']]}
                isSelected={selectedInterrogatories.has(int.id)}
                isSimplified={simplifiedSet.has(int.id)}
                isSavingOutside={savingSet.has(int.id)}
                language={clientLanguage}
                liRef={refs.current[`${int.number}`]}
                onSaveCommunicationItem={(lang, val, id) =>
                  onSaveCommunicationItem(int.id.toString(), lang, val, id)
                }
                onSelect={() => onSelect(int.id)}
                onSimplify={
                  canSimplify ? (english, spanish) => handleSimplifyOne(int.id, english, spanish) : undefined
                }
                questionType={questionType}
                readonly={readOnly}
                onTranslate={onTranslate}
                canSpanish={canSpanish}
              />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
